import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useReceiptVoucherContext } from "../../context/ReceiptVoucherContext";
import { formatDate } from "../../utils/dateFormatter";
import { BranchContactHeader } from "../../components/branchContact/BranchContact";
import "./receiptVoucherDetails.css";

const ReceiptVoucherDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getReceiptVoucher, deleteReceiptVoucher } =
    useReceiptVoucherContext();
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVoucherDetails = async () => {
      try {
        const response = await getReceiptVoucher(id);
        if (response.status === "success") {
          setVoucher(response.voucher);
        }
      } catch (err) {
        console.error("Error fetching voucher:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchVoucherDetails();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  const handleDelete = async () => {
    if (
      window.confirm(
        "هل أنت متأكد من حذف سند القبض؟\n\nسيتم عكس تأثير السند على رصيد الطرف."
      )
    ) {
      try {
        const response = await deleteReceiptVoucher(id);
        if (response.status === "success") {
          alert("✅ تم حذف سند القبض بنجاح");
          navigate("/receipt-vouchers");
        }
      } catch (err) {
        alert(err.response?.data?.message || "حدث خطأ أثناء حذف السند");
      }
    }
  };

  if (loading) {
    return (
      <div className="voucher-details-page">
        <div className="loading-container">
          <p>جارٍ تحميل تفاصيل السند...</p>
        </div>
      </div>
    );
  }

  if (!voucher) {
    return (
      <div className="voucher-details-page">
        <div className="error-container">
          <p>لم يتم العثور على السند</p>
          <Link to="/receipt-vouchers">العودة للسندات</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="voucher-details-page">
      <div className="voucher-container">
        {/* Action Buttons */}
        <div className="action-buttons no-print">
          <Link to="/receipt-vouchers" className="back-btn">
            ← العودة للسندات
          </Link>
          <div style={{ display: "flex", gap: "10px" }}>
            <button className="print-btn" onClick={handlePrint}>
              🖨️ طباعة السند
            </button>
            <button className="delete-btn" onClick={handleDelete}>
              🗑️ حذف السند
            </button>
          </div>
        </div>

        <BranchContactHeader branch={voucher.branch} />

        {/* Voucher Header */}
        <div className="voucher-header">
          <h1>سند قبض رقم #{voucher.id}</h1>
          <div className="voucher-date">
            التاريخ: {formatDate(voucher.created_at)}
          </div>
        </div>

        {/* Party Info */}
        <div className="voucher-info-section">
          <h2>
            معلومات {voucher.party_type === "supplier" ? "المورد" : "الزبون"}
          </h2>
          <div className="voucher-info-grid">
            <div className="voucher-info-item">
              <span className="label">الاسم:</span>
              <span className="value">
                <a
                  href={`/${voucher.party_type}/${
                    voucher.party_type === "supplier"
                      ? voucher.supplier_id
                      : voucher.customer_id
                  }`}
                  style={{
                    color: "#0066ff",
                    textDecoration: "none",
                    fontWeight: 500,
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.textDecoration = "underline")
                  }
                  onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
                >
                  {voucher.party_name}
                </a>
              </span>
            </div>
            <div className="voucher-info-item">
              <span className="label">الهاتف:</span>
              <span className="value">{voucher.party_phone}</span>
            </div>
          </div>
        </div>

        {/* Invoice Info */}
        <div className="voucher-info-section">
          <h2>معلومات الفاتورة</h2>
          <div className="voucher-info-grid">
            <div className="voucher-info-item">
              <span className="label">رقم الفاتورة:</span>
              <span className="value">
                <a
                  href={`/sales-invoice/${voucher.invoice_id}`}
                  style={{
                    color: "#0066ff",
                    textDecoration: "none",
                    fontWeight: 500,
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.textDecoration = "underline")
                  }
                  onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
                >
                  #{voucher.invoice_id}
                </a>
              </span>
            </div>
          </div>
        </div>

        {/* Payment Info */}
        <div className="voucher-info-section">
          <h2>معلومات الدفع</h2>
          <div className="voucher-info-grid">
            <div className="voucher-info-item">
              <span className="label">المبلغ المدفوع:</span>
              <span className="value amount">
                {parseFloat(voucher.amount).toFixed(2)} ₪
              </span>
            </div>
            <div className="voucher-info-item">
              <span className="label">نوع الدفع:</span>
              <span className="value">
                <span
                  className={`payment-badge ${
                    voucher.payment_type === "كاش"
                      ? "badge-cash"
                      : "badge-check"
                  }`}
                >
                  {voucher.payment_type === "كاش" ? "كاش" : "شيك"}
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* Check Info */}
        {voucher.payment_type === "شيك" && (
          <div className="voucher-info-section">
            <h2>معلومات الشيك</h2>
            <div className="voucher-info-grid">
              <div className="voucher-info-item">
                <span className="label">رقم الشيك:</span>
                <span className="value">{voucher.check_number}</span>
              </div>
              <div className="voucher-info-item">
                <span className="label">اسم البنك:</span>
                <span className="value">{voucher.check_bank}</span>
              </div>
              <div className="voucher-info-item">
                <span className="label">تاريخ الاستحقاق:</span>
                <span className="value">
                  {formatDate(voucher.check_due_date)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Notes */}
        {voucher.notes && (
          <div className="invoice-notes">
            <h2>ملاحظات</h2>
            <p>{voucher.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceiptVoucherDetails;
